/**
 * Portal Footer Component
 *
 * Professional footer for the Conditions Portal:
 * - Equal Housing Lender certification (if applicable)
 * - Company NMLS number
 * - Copyright notice
 */

import { Home } from 'lucide-react';
import type { ConditionsPortalCompany } from '@/types/conditions-portal';

interface PortalFooterProps {
  company: ConditionsPortalCompany | null;
}

export const PortalFooter = ({ company }: PortalFooterProps) => {
  const currentYear = new Date().getFullYear();

  if (!company) {
    return (
      <footer className="bg-gray-50 border-t py-4">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center text-xs text-gray-500">
            <span>&copy; {currentYear} Lendwise Mortgage Inc.</span>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gray-50 border-t py-4">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-xs text-gray-500">
          {/* Equal Housing Lender */}
          {company.equalHousingLender && (
            <div className="flex items-center gap-1.5">
              <Home className="h-4 w-4" />
              <span>Equal Housing Lender</span>
            </div>
          )}

          {/* Separator */}
          {company.equalHousingLender && company.nmls && (
            <span className="hidden sm:inline text-gray-300">|</span>
          )}

          {/* NMLS */}
          {company.nmls && <span>NMLS# {company.nmls}</span>}

          {/* Separator */}
          {(company.equalHousingLender || company.nmls) && (
            <span className="hidden sm:inline text-gray-300">|</span>
          )}

          {/* Copyright */}
          <span>
            &copy; {currentYear} {company.legalName || company.name}
          </span>
        </div>

        {/* Website */}
        {company.website && (
          <div className="text-center mt-2">
            <a
              href={company.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-gray-600 hover:underline"
            >
              {company.website.replace(/^https?:\/\//, '')}
            </a>
          </div>
        )}
      </div>
    </footer>
  );
};

export default PortalFooter;
